import React, { useState, useEffect, useContext } from "react";
import ReactDOM from "react-dom";
import { Container, Row, Col, Button, Tab, Tabs } from "react-bootstrap";
import {
  FaShoppingCart,
  FaCheckCircle,
  FaStar,
  FaRegStar,
  FaTimes,
  FaMinus,
  FaPlus,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "../../../assets/css/customer/productPage/SingleProductDetailPage.css";
import RelatedProducts from "./RelatedProducts";
import MyNavbar from "../../../components/general/navbar/MyNavbar";
import Footer from "../../../components/general/footer/Footer";
import { SingleProductSkeleton } from "../../../components/general/skeleton/SkeletonBlock";
import MyContext from "../../../context/MyContext";
import axios from "axios";
import Swal from "sweetalert2";
import { PLACEHOLDER_PRODUCT } from "../../../utils/placeholder";

const SingleProductDetailPage = () => {
  const navigate = useNavigate();
  const {
    baseUrl,
    baseUrlForFiles: ctxBaseUrlForFiles,
    baseUrlForFiles,
    selectedProduct,
    loggedInCustomer,
    authToken,
    cartProducts,
    setCartProducts,
  } = useContext(MyContext);
  const [showQtyModal, setShowQtyModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [inCart, setInCart] = useState(false);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("description");
  const filesUrl = baseUrlForFiles || ctxBaseUrlForFiles;

  // ── min order quantity + stock-aware presets ──────────────────
  const minQtyRaw = product?.min_order_quantity || 1;
  const minQty = Math.min(minQtyRaw, Number(product?.stock) || minQtyRaw);
  const presets = [minQty, minQty * 2, minQty * 5, minQty * 10].filter(
    (v, i, arr) => arr.indexOf(v) === i && v <= (product?.stock ?? Infinity)
  );

  // ── addProductToCart — safety net: agar 401 aaye to login pe bhej dein ──
  const addProductToCart = () => {
    if (quantity < minQty) {
      Swal.fire({ icon: "error", title: "Error", text: `Minimum order quantity is ${minQty}.` });
      return;
    }
    if (quantity > product.stock) {
      Swal.fire({ icon: "error", title: "Error", text: `Only ${product.stock} in stock.` });
      return;
    }

    const formData = new FormData();
    formData.append("kmproduct_id", product.id);
    formData.append("amountPerPiece", product.price);
    formData.append("quantity", quantity);
    formData.append("total_price", product.price * quantity);

    axios
      .post(`${baseUrl}/customer/cart/addProductToCart`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setInCart(true);
          setShowQtyModal(false);
          setShowToast(true);
          setTimeout(() => setShowToast(false), 2500);

          const newQuantity = existingCartItem
            ? existingCartItem.quantity + quantity
            : quantity;

          if (existingCartItem) {
            setCartProducts((prev) =>
              prev.map((c) =>
                c.kmproduct_id === product.id
                  ? {
                      ...c,
                      quantity: newQuantity,
                      total_price: newQuantity * product.price,
                    }
                  : c,
              ),
            );
          } else {
            setCartProducts((prev) => [
              ...prev,
              {
                kmproduct_id: product.id,
                kmproduct: product,
                quantity: quantity,
                amountPerPiece: product.price,
                total_price: product.price * quantity,
                isPurchased: false,
              },
            ]);
          }
          setQuantity(minQty);
        }
      })
      .catch((error) => {
        console.log("cart API error:", error);
        if (error.response?.status === 401) {
          setShowQtyModal(false);
          localStorage.setItem(
            "pendingAction",
            JSON.stringify({
              type: "cart",
              product,
              quantity,
              returnPath: window.location.pathname,
            }),
          );
          navigate("/customer/login");
          return;
        }
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to add product to cart.",
        });
      });
  };

  // ── API CALL ──────────────────────────────────────────────────
  const viewProductDetails = () => {
    setLoading(true);

    const formData = new FormData();
    formData.append("id", selectedProduct.id);

    axios
      .post(`${baseUrl}/customer/product/viewProductDetails`, formData)
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setProduct(response.data.product);
        }
        setLoading(false);
      })
      .catch((error) => {
        setErrorMessage("Failed to load product details.");
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load product details.",
        });
        setLoading(false);
      });
  };

  useEffect(() => {
    viewProductDetails();
  }, []);

  // ── HANDLERS ─────────────────────────────────────────────────
  const decrementQuantity = () => {
    if (quantity > 1) setQuantity(quantity - 1);
  };

  const incrementQuantity = () => {
    setQuantity(quantity + 1);
  };

  // ── openQuantityModal — login-gate: loggedInCustomer AUR authToken dono check ──
  const openQuantityModal = () => {
    if (!loggedInCustomer?.id || !authToken) {
      localStorage.setItem(
        "pendingAction",
        JSON.stringify({
          type: "cart",
          product,
          quantity: existingCartItem ? existingCartItem.quantity : minQty,
          returnPath: window.location.pathname,
        }),
      );
      navigate("/customer/login");
      return;
    }

    setQuantity(existingCartItem ? existingCartItem.quantity : minQty);
    setShowQtyModal(true);
  };

  const closeQuantityModal = () => {
    setShowQtyModal(false);
  };

  const selectTab = (k) => {
    setActiveTab(k);
  };

  const selectPresetQuantity = (val) => {
    setQuantity(val);
  };

  const decrementModalQuantity = () => {
    setQuantity((q) => Math.max(minQty, q - 1));
  };

  const incrementModalQuantity = () => {
    setQuantity((q) => Math.min(product.stock, (q || minQty) * 2));
  };

  const handleQuantityInputChange = (e) => {
    const val = parseInt(e.target.value);
    if (!isNaN(val) && val >= 1) setQuantity(val);
  };

  const handleImageError = (e) => {
    e.currentTarget.onerror = null; // loop band
    e.currentTarget.src = PLACEHOLDER_PRODUCT;
  };

  const existingCartItem = cartProducts?.find(
    (c) => c.kmproduct_id === product?.id && !c.isPurchased,
  );

  const renderStars = (rating) =>
    [...Array(5)].map((_, index) => (
      <span key={index} className="star">
        {index < rating ? <FaStar /> : <FaRegStar />}
      </span>
    ));

  return (
    <div className="product-detail-page">
      <MyNavbar />

      {loading ? (
        <Container>
          <SingleProductSkeleton />
        </Container>
      ) : (
        <Container>
          {/* Product Main Section */}
          <Row className="product-main-section">
            {/* Left — Images */}
            <Col lg={6} md={6} className="product-images-col">
              <div className="product-images-wrapper">
                <div className="main-image-container">
                  <img
                    src={
                      product?.image
                        ? `${baseUrlForFiles}/${product.image}`
                        : "https://via.placeholder.com/600"
                    }
                    alt={product?.name}
                    className="main-product-image"
                  />
                </div>
              </div>
            </Col>

            {/* Right — Product Info */}
            <Col lg={6} md={6} className="product-info-col">
              <div className="product-info-wrapper">
                <h1 className="product-detail-title">{product?.name}</h1>

                <div className="product-meta">
                  {product?.rating != null && (
                    <div className="rating-section">
                      <div className="stars-display">
                        {renderStars(product.rating)}
                      </div>
                      <span className="review-count">
                        ({product?.reviews?.length ?? 0} Reviews)
                      </span>
                    </div>
                  )}
                  {product?.inStock && (
                    <div className="stock-status">
                      <FaCheckCircle className="stock-icon" />
                      <span>In Stock</span>
                    </div>
                  )}
                </div>

                <div className="product-price-section">
                  <span className="current-price">
                    Rs {product?.price}
                    {product?.unit && <span> / {product.unit}</span>}
                  </span>
                  {product?.old_price && (
                    <span className="old-price">Rs {product.old_price}</span>
                  )}
                </div>

                <p className="product-description">{product?.description}</p>

                <div className="quantity-section">
                  <label className="quantity-label">Quantity:</label>
                  <div className="quantity-controls">
                    <button className="qty-btn" onClick={decrementQuantity}>
                      -
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      readOnly
                      className="qty-input"
                    />
                    <button className="qty-btn" onClick={incrementQuantity}>
                      +
                    </button>
                  </div>
                  <Button
                    className="add-to-cart-btn-detail"
                    onClick={openQuantityModal}
                  >
                    {existingCartItem ? (
                      <>
                        <FaCheckCircle className="me-2" />
                        In Cart ({existingCartItem.quantity})
                      </>
                    ) : (
                      <>
                        Add To Cart
                        <FaShoppingCart className="ms-2" />
                      </>
                    )}
                  </Button>
                </div>

                <div className="product-meta-info">
                  {product?.kmsubcategory?.kmcategory && (
                    <p>
                      <strong>Category:</strong>{" "}
                      <a href="#category">
                        {product.kmsubcategory.kmcategory.categoryname}
                      </a>
                    </p>
                  )}
                  {product?.kmsubcategory && (
                    <p>
                      <strong>Subcategory:</strong>{" "}
                      <a href="#subcategory">
                        {product.kmsubcategory.subcategoryname}
                      </a>
                    </p>
                  )}
                </div>
              </div>
            </Col>
          </Row>

          {/* Tabs Section */}
          <Row className="product-tabs-section">
            <Col lg={12}>
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => selectTab(k)}
                className="product-tabs"
              >
                <Tab eventKey="description" title="Description">
                  <div className="tab-content-wrapper">
                    <p>{product?.description}</p>
                  </div>
                </Tab>

                {product?.reviews?.length > 0 && (
                  <Tab
                    eventKey="reviews"
                    title={`Reviews (${product.reviews.length})`}
                  >
                    <div className="tab-content-wrapper">
                      <div className="reviews-list">
                        {product.reviews.map((review) => (
                          <div key={review.id} className="review-item">
                            <div className="review-avatar">
                              <div className="avatar-circle">
                                {review.name?.charAt(0)}
                              </div>
                            </div>
                            <div className="review-content">
                              <div className="review-header">
                                <div className="review-stars">
                                  {renderStars(review.rating)}
                                </div>
                              </div>
                              <h4 className="reviewer-name">{review.name}</h4>
                              <p className="review-date">{review.date}</p>
                              <p className="review-comment">{review.comment}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Tab>
                )}
              </Tabs>
            </Col>
          </Row>
        </Container>
      )}

      {/* ── Quantity Modal — portal ── */}
      {showQtyModal &&
        ReactDOM.createPortal(
          <div
            className="pcard-qty-overlay"
            onClick={() => closeQuantityModal()}
          >
            <div
              className="pcard-qty-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="pcard-qty-header">
                <h4 className="pcard-qty-title">Select Quantity</h4>
                <button
                  className="pcard-qty-close"
                  onClick={() => closeQuantityModal()}
                >
                  <FaTimes size={16} />
                </button>
              </div>

              {/* Already in cart banner */}
              {existingCartItem && (
                <div className="pcard-qty-in-cart-banner">
                  <FaCheckCircle size={13} />
                  Already in cart: <strong>{existingCartItem.quantity}</strong>
                </div>
              )}

              {/* Product Info */}
              <div className="pcard-qty-product">
                <img
                  src={`${filesUrl}/${product.image}`}
                  alt={product.name}
                  className="pcard-qty-image"
                  onError={(e) => handleImageError(e)}
                />
                <div className="pcard-qty-info">
                  <h5 className="pcard-qty-name">{product.name}</h5>
                  {product.subcategory && (
                    <span className="pcard-qty-category">
                      {product.subcategory}
                    </span>
                  )}
                  <div className="pcard-qty-price">
                    Rs. {product.price?.toLocaleString()}
                    {product.unit && (
                      <span className="pcard-qty-unit"> / {product.unit}</span>
                    )}
                  </div>
                  {product.oldPrice && (
                    <div className="pcard-qty-old-price">
                      Rs. {product.oldPrice?.toLocaleString()}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                    Min Order: {minQty} | In Stock: {product.stock}
                  </div>
                </div>
              </div>

              <hr className="pcard-qty-divider" />

              {/* Preset Buttons */}
              <p className="pcard-qty-label">Quick Select</p>
              <div className="pcard-qty-presets">
                {presets.map((val) => (
                  <button
                    key={val}
                    className={`pcard-qty-preset-btn ${quantity === val ? "active" : ""}`}
                    onClick={() => selectPresetQuantity(val)}
                    disabled={val > product.stock}
                  >
                    {val}
                  </button>
                ))}
              </div>

              {/* Manual +/- */}
              <div className="pcard-qty-controls">
                <button
                  className="pcard-qty-btn"
                  onClick={() => decrementModalQuantity()}
                >
                  <FaMinus size={12} />
                </button>
                <input
                  type="number"
                  className="pcard-qty-input"
                  value={quantity}
                  onChange={(e) => handleQuantityInputChange(e)}
                  min={minQty}
                />
                <button
                  className="pcard-qty-btn"
                  onClick={() => incrementModalQuantity()}
                >
                  <FaPlus size={12} />
                </button>
              </div>

              {/* Validation messages */}
              {quantity < minQty && (
                <p style={{ color: "#d33", fontSize: 12, marginTop: 4 }}>
                  Minimum order quantity is {minQty}.
                </p>
              )}
              {quantity > product.stock && (
                <p style={{ color: "#d33", fontSize: 12, marginTop: 4 }}>
                  Only {product.stock} in stock.
                </p>
              )}

              {/* Total */}
              <div className="pcard-qty-total">
                <span>Total:</span>
                <span className="pcard-qty-total-value">
                  Rs. {(product.price * quantity)?.toLocaleString()}
                </span>
              </div>

              {/* Footer */}
              <div className="pcard-qty-footer">
                <button
                  className="pcard-qty-cancel-btn"
                  onClick={() => closeQuantityModal()}
                >
                  Cancel
                </button>
                <button
                  className="pcard-qty-confirm-btn"
                  onClick={() => addProductToCart()}
                  disabled={quantity < minQty || quantity > product.stock}
                  style={quantity < minQty || quantity > product.stock ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                >
                  <FaShoppingCart size={13} style={{ marginRight: 6 }} />
                  {existingCartItem ? "Update Cart" : "Add to Cart"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
      {/* ── Toast — portal ── */}
      {showToast &&
        ReactDOM.createPortal(
          <div className="pcard-toast">
            <div className="pcard-toast-content">
              <FaCheckCircle className="pcard-toast-icon" />
              <span>
                {product.name} {existingCartItem ? "updated in" : "added to"}{" "}
                cart!
              </span>
            </div>
          </div>,
          document.body,
        )}
      <RelatedProducts />
      <Footer />
    </div>
  );
};

export default SingleProductDetailPage;
